<script lang="ts">
    import { onMount } from 'svelte';
    import { arcHelper } from './svg-helper';
    import type { SVGAnimateTransformElement } from './svg-helper';
    import type { Pattern} from './../pattern/pattern';
    import { PauseStep, Step } from './../pattern/step';

    import TurntableConfig from './TurntableConfig.svelte'; 
    import TurntableSvg from './TurntableSvg.svelte';
    import { Switch, Row, Col, Radio } from 'svelte-materialify';

    const svgNS = "http://www.w3.org/2000/svg";

    // // props
    export let pattern: Pattern;
    export let isPlaying: boolean = false;
    export let durationSample: number;
    export let stopPlaying: CallableFunction;

    const segmentSize = 160; // in degrees    
    const beatsInSample = 4; // @TODO 4 = 1x 4/4 bar

    const hardwareConfig = {
        recordHand: 'left',
        hamster: false,
    };

    const crossfaderPositionsBySide = {
        left: {open: '20 0', closed: '0 0'},
        right: {open: '80 0', closed: '100 0'},
    }

    let crossfaderPositions = crossfaderPositionsBySide.right;

    let animationValues = {
        record: {
            keytimes: [],
            values: []
        },
        crossfader: {
            keytimes: [],
            values: []
        }
    };

    let recordGroupInner: SVGElement;
    let crossfaderKnob: SVGElement;

    let recordRotationAnim: SVGAnimateTransformElement;
    let crossfaderAnim: SVGAnimateTransformElement;


    const calculateAnimationValues = (pattern: Pattern) => {
        calculateAnimationValuesForRecord(pattern);
        calculateAnimationValuesForCrossfader(pattern);
    }

    const calculateAnimationValuesForRecord = (pattern: Pattern) => {
        let keytimes = [];
        let values = [];

        for (let i = 0; i < pattern.steps.length; i++) {
            const step: Step = pattern.steps[i];

            if (i === 0) {
                const keytimeStart: number = step.startX / beatsInSample;
                keytimes.push(keytimeStart);

                const degreeStart: number = yToDegree(step.startY);
                values.push(degreeStart);
            }

            const degreeEnd: number = yToDegree(step.endY);
            const keytimeEnd: number = step.endX / beatsInSample;

            keytimes.push(keytimeEnd);
            values.push(degreeEnd);              
        }

        animationValues.record.keytimes = keytimes;
        animationValues.record.values = values;
    }

    const calculateAnimationValuesForCrossfader = (pattern: Pattern) => {
        const openCloseTime = 0.00001;
        
        let closedPosition: string = crossfaderPositions.closed;
        let openPosition: string = crossfaderPositions.open;
        // if (true) { // DEBUG
        //     closedPosition = 'C';
        //     openPosition = 'O';
        // }

        let keytimes: Array<number> = [];
        let values: Array<string> = [];

        for (let i = 0; i < pattern.steps.length; i++) {
            const step: Step = pattern.steps[i];
            
            let keytimeStart: number = step.startX / beatsInSample;
            keytimes.push(keytimeStart);

            let faderValue = step.startClosed ? closedPosition : openPosition;
            if (step instanceof PauseStep) {
                faderValue = closedPosition;
            }
            values.push(faderValue);

            // add extra move for close->open
            if (step.startClosed) {
                keytimes.push(keytimeStart + openCloseTime);
                values.push(openPosition);
            }

            const keytimeEnd: number = step.endX / beatsInSample;

            // add extra move for open->close
            if (step.endClosed) {
                keytimes.push(keytimeEnd - openCloseTime);
                values.push(openPosition);
            }


            faderValue = step.endClosed ? closedPosition : openPosition;
            if (step instanceof PauseStep) {
                faderValue = closedPosition;
            }
            
            keytimes.push(keytimeEnd);
            values.push(faderValue);
        }

        // remove duplicates
        let keytimesFiltered: Array<number> = [], valuesFiltered: Array<string> = [];
        let keyTimeLast: number = -1, valueLast: string = '';
        for (let i = 0; i < keytimes.length; i++) {
            const keytime: number = keytimes[i];
            const value: string = values[i];

            if (keytime != keyTimeLast || value != valueLast) {
                keytimesFiltered.push(keytime);
                valuesFiltered.push(value);
            }

            keyTimeLast = keytime;
            valueLast = value;
        }

        // console.log('[FILTERED] xFader keytimes:', keytimesFiltered);
        // console.log('[FILTERED] xFader values:', valuesFiltered);        
        animationValues.crossfader.keytimes = keytimesFiltered;
        animationValues.crossfader.values = valuesFiltered;
    }

    const yToDegree = (y: number): number => {
        return Math.round(y * segmentSize);
    };


    const createRecordAnimation = () => {
        const keytimes = animationValues.record.keytimes;
        const values = animationValues.record.values;

        recordRotationAnim = document.createElementNS(svgNS, 'animateTransform') as unknown as SVGAnimateTransformElement;
        
        recordRotationAnim.setAttributeNS(null, 'id', 'recordRotationAnim');
        recordRotationAnim.setAttributeNS(null, 'attributeType', 'XML');
        recordRotationAnim.setAttributeNS(null, 'attributeName', 'transform');
        recordRotationAnim.setAttributeNS(null, 'type', 'rotate');
        recordRotationAnim.setAttributeNS(null, 'repeatCount', 'indefinite');
        recordRotationAnim.setAttributeNS(null, 'restart', 'always');

        recordRotationAnim.setAttributeNS(null, 'keytimes', keytimes.join(";"));
        recordRotationAnim.setAttributeNS(null, 'values', values.join(";"));
        recordRotationAnim.setAttributeNS(null, 'dur', durationSample + 's');

        recordGroupInner.appendChild(recordRotationAnim);

        recordRotationAnim.beginElement();
    };
    const removeRecordAnimation = () => {
        recordRotationAnim = document.getElementById("recordRotationAnim") as unknown as SVGAnimateTransformElement;
        if (recordRotationAnim) {
            recordRotationAnim.remove();
        }
    };

    const createCrossfaderAnimation = () => {
        const keytimes = animationValues.crossfader.keytimes;
        const values = animationValues.crossfader.values;

        crossfaderAnim = document.createElementNS(svgNS, 'animateTransform') as unknown as SVGAnimateTransformElement;
        
        crossfaderAnim.setAttributeNS(null, 'id', 'crossfaderAnim');
        crossfaderAnim.setAttributeNS(null, 'attributeType', 'XML');
        crossfaderAnim.setAttributeNS(null, 'attributeName', 'transform');
        crossfaderAnim.setAttributeNS(null, 'type', 'translate');
        crossfaderAnim.setAttributeNS(null, 'repeatCount', 'indefinite');
        crossfaderAnim.setAttributeNS(null, 'restart', 'always');
        crossfaderAnim.setAttributeNS(null, 'keytimes', keytimes.join(";"));
        crossfaderAnim.setAttributeNS(null, 'values', values.join(";"));
        crossfaderAnim.setAttributeNS(null, 'dur', durationSample + 's');
        
        crossfaderKnob.appendChild(crossfaderAnim);    

        crossfaderAnim.beginElement();
    };
    const removeCrossfaderAnimation = () => {
        crossfaderAnim = document.getElementById("crossfaderAnim") as unknown as SVGAnimateTransformElement;
        if (crossfaderAnim) {
            crossfaderAnim.remove();
        }
    };

    const generateRecordSegmentArc = () => {
        const angleStop = 360; // 360 ^= needle position
        const angleStart = 360 - segmentSize;

        let seg = document.createElementNS(svgNS, "path");
        seg.setAttributeNS(null, "id", "sampleSegment1");
        seg.setAttributeNS(null, "class", "sampleSegment");
        seg.setAttributeNS(null, "stroke", "#446688");
        seg.setAttributeNS(null, "stroke-width", "100");
        
        const pathDescription = arcHelper.describeArc(0, 0, 100, angleStart, angleStop);
        seg.setAttributeNS(null, "d", pathDescription);

        const segments = document.getElementById("segments");
        segments.appendChild(seg);
    }

    const setStartPosition = (pattern: Pattern) => {
        if (!recordGroupInner) { return; }

        const firstStep: Step = pattern.steps[0];
        
        const startDeg = yToDegree(firstStep.startY);
        recordGroupInner.setAttribute('transform', `rotate(${startDeg})`);

        const crossfaderPosition = firstStep.startClosed ? crossfaderPositions.closed : crossfaderPositions.open;
        crossfaderKnob.setAttribute('transform', `translate(${crossfaderPosition})`);
    }

    const togglePlayStatus = (isPlaying: boolean) => {
        if (isPlaying) {
            createRecordAnimation();
            createCrossfaderAnimation();
        } else {
            removeRecordAnimation();
            removeCrossfaderAnimation();
        }
    };

    const updateCrossfaderPositions = (recordHand: string, hamster: boolean) => {
        stopPlaying();
        if (recordHand == 'right') {
            if (hamster) {
                crossfaderPositions = crossfaderPositionsBySide.right;
            } else {
                crossfaderPositions = crossfaderPositionsBySide.left;
            }
        } else {
            if (hamster) {
                crossfaderPositions = crossfaderPositionsBySide.left;
            } else {
                crossfaderPositions = crossfaderPositionsBySide.right;
            }
        }

        if (pattern) {
            calculateAnimationValues(pattern);
            setStartPosition(pattern);
        }
    }

    onMount(async () => {
		recordGroupInner = document.getElementById("recordGroupInner") as unknown as SVGElement;
        crossfaderKnob = document.getElementById("crossfaderKnob") as unknown as SVGElement;

        generateRecordSegmentArc();
	});

    $: if (pattern) {
        calculateAnimationValues(pattern);
        setStartPosition(pattern);
    } 
    $: togglePlayStatus(isPlaying);
    $: updateCrossfaderPositions(hardwareConfig.recordHand, hardwareConfig.hamster);

</script>



<Row dense>
    <Col cols={12} sm={8}>
        <TurntableSvg 
            recordHand={hardwareConfig.recordHand} 
            hamster={hardwareConfig.hamster} />
    </Col>
    <Col cols={12} sm={4}>
        <TurntableConfig 
            bind:recordHand={hardwareConfig.recordHand} 
            bind:hamster={hardwareConfig.hamster} 
            />
    </Col>
</Row>
